import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
    Globe, Database, Shield,
    Wallet, BookOpen, HelpCircle, ChevronLeft, ChevronRight,
    Settings, Folder, Activity, Server, HardDrive, FileText,
    ShieldCheck, Lock, Users, GitBranch, Key, Monitor, DollarSign, Network, Bot
} from 'lucide-react';

const SECTIONS = [
    {
        label: null, // No header for main section
        items: [
            { id: 'projects', label: 'Projects', icon: Folder, path: '/dashboard?tab=projects' },
            { id: 'services', label: 'Services', icon: Server, path: '/dashboard?tab=services' },
            { id: 'agents', label: 'AI Agents', icon: Bot, path: '/agents' },
            { id: 'redis', label: 'Redis Datastores', icon: Database, path: '/dashboard?tab=redis' },
            { id: 'volumes', label: 'Volumes', icon: HardDrive, path: '/dashboard?tab=volumes' },
            { id: 'domains', label: 'Domains', icon: Globe, path: '/dashboard?tab=domains' },
            { id: 'docs', label: 'API Docs', icon: FileText, path: '/docs/api' }
        ]
    },
    {
        label: 'CONFIGURATION',
        items: [
            { id: 'networks', label: 'Networks', icon: Network, path: '/networks' },
            { id: 'build-queue', label: 'Build Queue', icon: GitBranch, path: '/build-queue' },
            { id: 'env-groups', label: 'Env Groups', icon: Database, path: '/dashboard?tab=env-groups' },
            { id: 'security', label: 'Security', icon: Shield, path: '/dashboard?tab=security' },
            { id: 'api-keys', label: 'API Keys & Rate Limits', icon: Key, path: '/dashboard?tab=api-keys' },
            { id: 'compliance', label: 'Compliance', icon: ShieldCheck, path: '/compliance' },
            { id: 'privacy', label: 'Privacy Settings', icon: Lock, path: '/privacy-settings' },
        ]
    },
    {
        label: 'WORKSPACE',
        items: [
            { id: 'wallet', label: 'Billing', icon: Wallet, path: '/wallet' },
            { id: 'budget', label: 'Budget Settings', icon: DollarSign, path: '/budget' },
            { id: 'rate-limits', label: 'Rate Limits', icon: Activity, path: '/rate-limits' },
            { id: 'sessions', label: 'Active Sessions', icon: Monitor, path: '/sessions' },
            { id: 'profile', label: 'Settings', icon: Settings, path: '/profile' },
            { id: 'admin-dashboard', label: 'Admin Panel', icon: Users, path: '/admin/dashboard' },
        ]
    },
];

const BOTTOM_ITEMS = [
    { id: 'docs', label: 'Docs', icon: BookOpen, action: 'docs' },
    { id: 'support', label: 'Contact Support', icon: HelpCircle, action: 'support' },
];

const Sidebar = ({ activeSection, onSectionChange, collapsed, mobileOpen, onCloseMobile, onToggleCollapse, onOpenDocs, counts = {} }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();

    const handleItemClick = (item) => {
        if (item.path) {
            // Only use onSectionChange if we are currently on the Dashboard page and it's a dashboard tab
            if (location.pathname === '/dashboard' && item.path.startsWith('/dashboard?tab=')) {
                if (onSectionChange) onSectionChange(item.id);
                // Also update URL so refresh maintains state
                navigate(item.path, { replace: true });
            } else {
                navigate(item.path);
            }
        }
        if (mobileOpen && onCloseMobile) {
            onCloseMobile();
        }
    };

    const isActive = (item) => {
        if (item.path && !item.path.startsWith('/dashboard')) {
            return location.pathname === item.path;
        }
        return activeSection === item.id;
    };

    return (
        <>
            {/* Mobile Backdrop */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 bg-root/80 z-40 md:hidden backdrop-blur-sm"
                    onClick={onCloseMobile}
                />
            )}

            <aside
                className={`fixed left-0 top-20 bottom-0 bg-card border-r border-inner flex flex-col z-50 transition-all duration-300 
                ${mobileOpen ? 'translate-x-0 w-[240px] shadow-2xl' : '-translate-x-full md:translate-x-0'} 
                ${!mobileOpen && collapsed ? 'md:w-[68px]' : !mobileOpen ? 'md:w-[240px]' : 'w-[240px]'}`}
            >
                {/* Collapse Toggle (Desktop only) */}
                <button
                    onClick={onToggleCollapse}
                    className="hidden md:flex absolute -right-3 top-6 w-6 h-6 bg-card border border-inner rounded-full items-center justify-center text-muted hover:text-primary hover:bg-hover shadow-sm z-50 transition"
                >
                    {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
                </button>

                {/* Navigation Sections */}
                <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
                    {SECTIONS.map((section, sIdx) => (
                        <div key={sIdx} className={sIdx > 0 ? 'mt-5' : ''}>
                            {/* Section Label */}
                            {section.label && (!collapsed || mobileOpen) && (
                                <div className="px-3 mb-2 text-[11px] font-bold text-muted uppercase tracking-widest">
                                    {section.label}
                                </div>
                            )}
                            {section.label && collapsed && !mobileOpen && (
                                <div className="border-t border-inner mx-2 mb-2" />
                            )}

                            {/* Section Items */}
                            {section.items
                                .filter((item) => item.id !== 'admin-dashboard' || (user?.platform_role?.priority || 0) > 0)
                                .map((item) => {
                                const Icon = item.icon;
                                const active = isActive(item);
                                const count = counts[item.id];
                                const labelsVisible = !collapsed || mobileOpen;

                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => handleItemClick(item)}
                                        title={!labelsVisible ? item.label : undefined}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all group ${active
                                            ? 'bg-hover text-indigo font-bold border border-inner'
                                            : 'text-body hover:bg-hover hover:text-primary border border-transparent'
                                            }`}
                                    >
                                        <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${active ? 'text-indigo' : 'text-muted group-hover:text-heading'}`} />
                                        {labelsVisible && (
                                            <>
                                                <span className="flex-1 text-left truncate">{item.label}</span>
                                                {count !== undefined && count > 0 && (
                                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${active ? 'bg-indigo/10 text-indigo' : 'bg-input text-muted'
                                                        }`}>
                                                        {count}
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </nav>

                {/* Bottom Section */}
                <div className="border-t border-inner p-3 space-y-1">
                    {BOTTOM_ITEMS.map((item) => {
                        const Icon = item.icon;
                        const labelsVisible = !collapsed || mobileOpen;
                        return (
                            <button
                                key={item.id}
                                title={!labelsVisible ? item.label : undefined}
                                onClick={() => {
                                    if (item.action === 'docs') window.open('/docs', '_blank');
                                    if (item.action === 'support') window.open('/contact', '_blank');
                                    if (mobileOpen && onCloseMobile) onCloseMobile();
                                }}
                                className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-body hover:text-primary hover:bg-hover transition"
                            >
                                <Icon className="w-[18px] h-[18px] flex-shrink-0 text-muted group-hover:text-heading" />
                                {labelsVisible && <span className="truncate">{item.label}</span>}
                            </button>
                        );
                    })}
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
